/* ============================================================
   VITALCASA CRM — Configuración Scraping
   Layout sin scroll: header credenciales + 2 columnas
   ============================================================ */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../components/ThemeProvider';
import api from '../services/api';
import { dialogs } from '../context/DialogContext';
import { normalizeSearchText } from '../utils/searchText';
import usePermiso from '../hooks/usePermiso';

// ── Constantes ────────────────────────────────────────────────────────────────
const OPERACIONES = [
  { value: 'comprar',  label: 'Comprar' },
  { value: 'alquiler', label: 'Alquilar' },
];
const TIPOS_INMUEBLE = [
  { value: '',          label: '— Todos —' },
  { value: 'piso',      label: 'Piso' },
  { value: 'apartamento', label: 'Apartamento' },
  { value: 'atico',     label: 'Ático' },
  { value: 'duplex',    label: 'Dúplex' },
  { value: 'estudio',   label: 'Estudio' },
  { value: 'chalet',    label: 'Chalet' },
  { value: 'casa',      label: 'Casa' },
  { value: 'villa',     label: 'Villa' },
  { value: 'adosado',   label: 'Adosado' },
  { value: 'pareado',   label: 'Pareado' },
  { value: 'finca',     label: 'Finca' },
  { value: 'local',     label: 'Local' },
  { value: 'oficina',   label: 'Oficina' },
  { value: 'terreno',   label: 'Terreno' },
  { value: 'nave',      label: 'Nave' },
  { value: 'garage',    label: 'Garaje' },
];
const EXTRAS_LISTA = [
  { key: 'piscina',            label: 'Piscina',      icon: '🏊' },
  { key: 'garaje',             label: 'Garaje',       icon: '🚗' },
  { key: 'terraza',            label: 'Terraza',      icon: '🌿' },
  { key: 'jardin',             label: 'Jardín',       icon: '🌳' },
  { key: 'ascensor',           label: 'Ascensor',     icon: '🛗' },
  { key: 'aire_acondicionado', label: 'A/C',          icon: '❄️' },
  { key: 'calefaccion',        label: 'Calefacción',  icon: '🔥' },
  { key: 'amueblado',          label: 'Amueblado',    icon: '🪑' },
  { key: 'trastero',           label: 'Trastero',     icon: '📦' },
  { key: 'primera_linea',      label: '1ª Línea',     icon: '🌊' },
  { key: 'vistas_mar',         label: 'Vista Mar',    icon: '🏖️' },
];

// Portales configurables (fácil añadir más)
const PORTALES_CONFIG = [
  { id: 'fotocasa',  nombre: 'Fotocasa',  emoji: '🔶', campo_email: 'fotocasa_email',  campo_pass: 'fotocasa_password',  campo_updated: 'fotocasa_cookie_updated_at', campo_cookie: 'fotocasa_cookie_string' },
  { id: 'idealista', nombre: 'Idealista', emoji: '🏠', campo_email: 'idealista_email', campo_pass: 'idealista_password', campo_updated: 'idealista_cookie_updated_at', campo_cookie: 'idealista_cookie_string' },
  { id: 'pisos',     nombre: 'Pisos.com', emoji: '🔑', campo_email: 'pisos_email',     campo_pass: 'pisos_password',     campo_updated: 'pisos_cookie_updated_at',  campo_cookie: 'pisos_cookie_string' },
];

const PORTAL_COLORS = {
  fotocasa:    { bg: 'rgba(249,115,22,0.15)',  border: '#f97316', text: '#f97316' },
  idealista:   { bg: 'rgba(14,165,233,0.15)',  border: '#0ea5e9', text: '#0ea5e9' },
  pisos:       { bg: 'rgba(168,85,247,0.15)',  border: '#a855f7', text: '#a855f7' },
  kyero:       { bg: 'rgba(16,185,129,0.15)',  border: '#10b981', text: '#10b981' },
  habitaclia:  { bg: 'rgba(6,182,212,0.15)',   border: '#06b6d4', text: '#06b6d4' },
  milanuncios: { bg: 'rgba(245,158,11,0.15)',  border: '#f59e0b', text: '#f59e0b' },
  thinkspain:  { bg: 'rgba(236,72,153,0.15)',  border: '#ec4899', text: '#ec4899' },
  todopisos:   { bg: 'rgba(132,204,22,0.15)',  border: '#84cc16', text: '#84cc16' },
};

// Portales basados en URL (sin selector de poblaciones)
const URL_PORTAL_MAP = {
  idealista:   { field: '_urls_idealista',   extrasKey: 'urls_idealista',   firstKey: 'url_idealista',   placeholder: 'https://www.idealista.com/venta-viviendas/denia-alicante/' },
  pisos:       { field: '_urls_pisos',       extrasKey: 'urls_pisos',       firstKey: 'url_pisos',       placeholder: 'https://www.pisos.com/venta/pisos-denia/' },
  kyero:       { field: '_urls_kyero',       extrasKey: 'urls_kyero',       firstKey: 'url_kyero',       placeholder: 'https://www.kyero.com/es/property-for-sale/denia' },
  habitaclia:  { field: '_urls_habitaclia',  extrasKey: 'urls_habitaclia',  firstKey: 'url_habitaclia',  placeholder: 'https://www.habitaclia.com/comprar-casas-en-denia.htm' },
  milanuncios: { field: '_urls_milanuncios', extrasKey: 'urls_milanuncios', firstKey: 'url_milanuncios', placeholder: 'https://www.milanuncios.com/inmobiliaria/denia-alicante/' },
  thinkspain:  { field: '_urls_thinkspain',  extrasKey: 'urls_thinkspain',  firstKey: 'url_thinkspain',  placeholder: 'https://www.thinkspain.com/property-for-sale/denia' },
  todopisos:   { field: '_urls_todopisos',   extrasKey: 'urls_todopisos',   firstKey: 'url_todopisos',   placeholder: 'https://www.todopisos.com/comprar/denia' },
};

const PORTAL_BADGE = {
  fotocasa: 'F', idealista: 'I', pisos: 'P',
  kyero: 'K', habitaclia: 'H', milanuncios: 'M', thinkspain: 'TS', todopisos: 'TP',
};

const BUSQUEDA_VACIA = {
  nombre: '', portal: 'fotocasa', operacion: 'comprar', tipo: '',
  provincia: '', provincia_id: '', poblaciones: [],
  precio_min: '', precio_max: '', hab_min: '', banos_min: '',
  m2_min: '', m2_max: '', parcela_min: '', parcela_max: '',
  solo_particulares: false, extras: {}, activo: true,
  // Campos auxiliares UI (no se envían directamente al backend)
  _urls_idealista: '', _urls_pisos: '', _urls_kyero: '',
  _urls_habitaclia: '', _urls_milanuncios: '', _urls_thinkspain: '', _urls_todopisos: '',
};

// ── MultiSelect Poblaciones ────────────────────────────────────────────────────
function MultiSelectPoblaciones({ poblaciones, seleccionadas, onChange, t }) {
  const [busqueda, setBusqueda] = useState('');
  const [abierto,  setAbierto]  = useState(false);
  const ref = useRef(null);
  const filtradas = (() => {
    if (!busqueda) return poblaciones;
    const q = normalizeSearchText(busqueda);
    const starts  = poblaciones.filter(p => normalizeSearchText(p.nombre).startsWith(q));
    const contains = poblaciones.filter(p => !normalizeSearchText(p.nombre).startsWith(q) && normalizeSearchText(p.nombre).includes(q));
    return [...starts, ...contains];
  })();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = nombre => onChange(
    seleccionadas.includes(nombre) ? seleccionadas.filter(x => x !== nombre) : [...seleccionadas, nombre]
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setAbierto(o => !o)} style={{
        backgroundColor: t.inputBg, border: `1.5px solid ${t.inputBorder}`,
        borderRadius: 6, padding: '5px 8px', cursor: 'pointer',
        minHeight: 32, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
      }}>
        {seleccionadas.length === 0
          ? <span style={{ color: t.textPlaceholder, fontSize: 12 }}>Selecciona…</span>
          : seleccionadas.map(n => (
            <span key={n} style={{ backgroundColor: t.accentBlue, color: '#fff', borderRadius: 3, padding: '1px 5px', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}>
              {n}
              <span onClick={e => { e.stopPropagation(); toggle(n); }} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
            </span>
          ))
        }
        <span style={{ marginLeft: 'auto', color: t.textMuted, fontSize: 10 }}>▾</span>
      </div>
      {abierto && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, backgroundColor: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', marginTop: 2 }}>
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${t.borderLight}` }}>
            <input autoFocus placeholder="Buscar…" value={busqueda} onChange={e => setBusqueda(e.target.value)}
              style={{ width: '100%', backgroundColor: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: 4, padding: '4px 6px', color: t.textPrimary, fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            {filtradas.length === 0
              ? <div style={{ padding: '8px', color: t.textMuted, fontSize: 11, textAlign: 'center' }}>Sin resultados</div>
              : filtradas.map(p => (
                <div key={p.id} onClick={() => toggle(p.nombre)}
                  style={{ padding: '5px 10px', cursor: 'pointer', fontSize: 12, color: seleccionadas.includes(p.nombre) ? t.accentBlue : t.textPrimary, backgroundColor: seleccionadas.includes(p.nombre) ? t.accentBlueLight : 'transparent', display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => { if (!seleccionadas.includes(p.nombre)) e.currentTarget.style.backgroundColor = t.bgCardHover; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = seleccionadas.includes(p.nombre) ? t.accentBlueLight : 'transparent'; }}
                >
                  <span style={{ width: 12, height: 12, borderRadius: 2, border: `1.5px solid ${seleccionadas.includes(p.nombre) ? t.accentBlue : t.border}`, backgroundColor: seleccionadas.includes(p.nombre) ? t.accentBlue : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8 }}>
                    {seleccionadas.includes(p.nombre) ? '✓' : ''}
                  </span>
                  {p.nombre}
                </div>
              ))
            }
          </div>
          {seleccionadas.length > 0 && (
            <div style={{ padding: '4px 8px', borderTop: `1px solid ${t.borderLight}`, textAlign: 'right' }}>
              <button onClick={() => onChange([])} style={{ color: t.accentRed, fontSize: 10, cursor: 'pointer', background: 'none', border: 'none' }}>Limpiar</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Autocompletado Provincia ───────────────────────────────────────────────────
function AutocompleteProvincia({ value, onChange, onSeleccionada, provincias, t }) {
  const [open, setOpen] = useState(false);
  const [q,    setQ]    = useState(value || '');
  const ref = useRef(null);
  const filtradas = q.length >= 1 ? provincias.filter(p => p.nombre.toLowerCase().includes(q.toLowerCase())) : provincias;

  useEffect(() => { setQ(value || ''); }, [value]);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sel = p => { setQ(p.nombre); onChange(p.nombre); onSeleccionada(p.id); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input value={q} onFocus={() => setOpen(true)} placeholder="Alicante, Valencia…"
        onChange={e => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        style={{ width: '100%', backgroundColor: t.inputBg, border: `1.5px solid ${t.inputBorder}`, borderRadius: 6, padding: '5px 8px', color: t.textPrimary, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
      {open && filtradas.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, backgroundColor: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', marginTop: 2, maxHeight: 160, overflowY: 'auto' }}>
          {filtradas.map(p => (
            <div key={p.id} onClick={() => sel(p)} style={{ padding: '5px 10px', cursor: 'pointer', fontSize: 12, color: t.textPrimary }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = t.bgCardHover}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              {p.nombre}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Toggle ─────────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, t, size = 'sm' }) {
  const w = size === 'sm' ? 32 : 40, h = size === 'sm' ? 18 : 22, d = size === 'sm' ? 12 : 16;
  return (
    <div onClick={() => onChange(!value)} style={{ width: w, height: h, borderRadius: h / 2, cursor: 'pointer', backgroundColor: value ? t.accentBlue : t.border, position: 'relative', transition: 'background-color 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: (h - d) / 2, width: d, height: d, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s', left: value ? w - d - (h - d) / 2 : (h - d) / 2 }} />
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function ConfiguracionScraping() {
  const { theme } = useTheme();
  const t = theme.colors;
  const { puede } = usePermiso();
  const veCredenciales = puede('cfg_scr_credenciales', 'ver');
  const modCredenciales = puede('cfg_scr_credenciales', 'modificar');
  const veBusqueda = puede('cfg_scr_busqueda', 'ver');
  const modBusqueda = puede('cfg_scr_busqueda', 'modificar');
  const veCookies = puede('cfg_scr_cookies', 'ver');

  const [busquedas,    setBusquedas]    = useState([]);
  const [config,       setConfig]       = useState({});
  const [provincias,   setProvincias]   = useState([]);
  const [poblaciones,  setPoblaciones]  = useState([]);
  const [form,         setForm]         = useState({ ...BUSQUEDA_VACIA });
  const [editandoId,   setEditandoId]   = useState(null);
  const [filtroPortal, setFiltroPortal] = useState('todos'); // todos | fotocasa | idealista | pisos
  const [guardando,    setGuardando]    = useState(false);
  const [guardandoCfg, setGuardandoCfg] = useState(false);
  const [error,        setError]        = useState('');
  const [exito,        setExito]        = useState('');
  const [ejecutando,   setEjecutando]   = useState({});
  const [jobEstados,   setJobEstados]   = useState({});  // busquedaId → {estado, msg, total, procesados, nuevos}
  const [showAdvanced,     setShowAdvanced]     = useState(false);
  const [showCredenciales, setShowCredenciales] = useState(false);
  const [showCookieHelp,   setShowCookieHelp]   = useState(false);
  const [showIdealistaCookieHelp, setShowIdealistaCookieHelp] = useState(false);
  const [testandoIdealista, setTestandoIdealista] = useState(false);
  const [testIdealistaResult, setTestIdealistaResult] = useState(null);
  const [testando,         setTestando]         = useState(false);
  const [testResult,    setTestResult]   = useState(null);
  const [renovando,     setRenovando]    = useState(false);
  const [renovResult,   setRenovResult]  = useState(null);
  const [syncEstado,    setSyncEstado]   = useState(null);
  const pollingRefs = useRef({});

  const cargarDatos = useCallback(async () => {
    try {
      const [resBusc, resCfg, resProv, resSync] = await Promise.all([
        api.get('/scraping/busquedas'),
        api.get('/scraping/config'),
        api.get('/scraping/provincias'),
        api.get('/scraping/estado-sincronizacion').catch(() => ({ data: null })),
      ]);
      setBusquedas(resBusc.data);
      setConfig(resCfg.data);
      setProvincias(resProv.data);
      setSyncEstado(resSync.data);
    } catch (e) {
      setError('Error cargando datos: ' + (e.response?.data?.error || e.message));
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      Object.values(pollingRefs.current).forEach(clearInterval);
    };
  }, []);

  useEffect(() => {
    if (!form.provincia_id) { setPoblaciones([]); return; }
    api.get(`/scraping/poblaciones?provincia_id=${form.provincia_id}`)
      .then(r => setPoblaciones(r.data))
      .catch(() => setPoblaciones([]));
  }, [form.provincia_id]);

  const mostrarExito = msg => { setExito(msg); setError(''); setTimeout(() => setExito(''), 3000); };

  const probarConexion = async () => {
    setTestando(true); setTestResult(null);
    try {
      const r = await api.get('/scraping/test-conexion');
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, estado: 'error', mensaje: e.message });
    } finally { setTestando(false); }
  };

  const probarIdealistaSesion = async () => {
    setTestandoIdealista(true);
    setTestIdealistaResult(null);
    try {
      const r = await api.get('/scraping/test-idealista-sesion');
      setTestIdealistaResult(r.data);
    } catch (e) {
      setTestIdealistaResult({ ok: false, msg: e.response?.data?.error || e.message });
    } finally {
      setTestandoIdealista(false);
    }
  };

  const renovarCookies = async () => {
    const cookieIdealista = String(config.idealista_cookie_string || '').trim();
    const tieneIdealista = Boolean(config.idealista_email && config.idealista_password);
    const tieneFotocasa = Boolean(config.fotocasa_email && config.fotocasa_password);

    if (cookieIdealista.length >= 80) {
      if (!tieneFotocasa) {
        setRenovResult({
          ok: true,
          mensaje: '✅ Cookie Idealista guardada. No uses 🔄 Renovar: pulsa 📞 Sesión Idealista y reimporta el anuncio.',
        });
        return;
      }
      setRenovResult({
        ok: null,
        mensaje: '⏳ Renovando solo Fotocasa. Idealista: ya tienes cookie → 📞 Sesión Idealista.',
      });
    } else if (tieneIdealista && !tieneFotocasa) {
      setRenovResult({
        ok: false,
        mensaje:
          'Idealista: pega la cookie en el campo de abajo → 💾 Guardar → 📞 Sesión Idealista. '
          + 'El botón 🔄 Renovar no sirve para Idealista.',
      });
      return;
    } else if (tieneIdealista && tieneFotocasa) {
      setRenovResult({
        ok: null,
        mensaje: '⏳ Renovando solo Fotocasa. Idealista: cookie manual + 📞 Sesión Idealista.',
      });
    }

    if (syncEstado && syncEstado.puppeteerLoginEnabled === false) {
      setRenovResult({
        ok: false,
        mensaje:
          'El login con Puppeteer en el servidor está desactivado (Fotocasa bloquea datacenters). ' +
          'Usa la extensión Chrome: carpeta del repo extensions/fotocasa-cookie-bridge. ' +
          'En Railway define FOTOCASA_COOKIE_INGEST_SECRET (mín. 16 caracteres) y el mismo valor en opciones de la extensión. ' +
          'URL POST: ' + (syncEstado.ingestFullUrl || '/api/public/fotocasa-cookies'),
      });
      return;
    }
    if (!config.fotocasa_email || !config.fotocasa_password) {
      setRenovResult({ ok: false, mensaje: '⚠️ Introduce primero el email y contraseña de Fotocasa y pulsa Guardar.' });
      return;
    }
    setRenovando(true);
    setRenovResult({ ok: null, mensaje: '⏳ Iniciando login automático con Puppeteer… (puede tardar 30-60 s)' });
    try {
      await api.post('/scraping/renovar-cookies');
      // Polling del estado de renovación
      let intentos = 0;
      const poll = setInterval(async () => {
        intentos++;
        try {
          const r = await api.get('/scraping/estado/renovacion');
          const est = r.data;
          if (est?.estado === 'ok') {
            setRenovResult({ ok: true, mensaje: est.msg });
            clearInterval(poll);
            setRenovando(false);
            await cargarDatos(); // refrescar fecha de renovación
          } else if (est?.estado === 'error') {
            setRenovResult({ ok: false, mensaje: est.msg });
            clearInterval(poll);
            setRenovando(false);
          } else if (intentos > 60) { // 90s timeout
            setRenovResult({ ok: false, mensaje: '⏱ Timeout: la renovación tardó demasiado. Comprueba los logs de Railway.' });
            clearInterval(poll);
            setRenovando(false);
          }
        } catch { /* ignorar */ }
      }, 1500);
    } catch (e) {
      const d = e.response?.data;
      setRenovResult({ ok: false, mensaje: d?.mensaje || d?.error || ('Error: ' + e.message) });
      setRenovando(false);
    }
  };
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleExtra = key => setForm(f => ({ ...f, extras: { ...f.extras, [key]: !f.extras[key] } }));

  const guardarBusqueda = async () => {
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio.');
      await dialogs.alert('⚠️ Falta el nombre de la búsqueda.');
      return;
    }

    let payload = { ...form };
    // Eliminar todos los campos auxiliares _urls_*
    Object.keys(URL_PORTAL_MAP).forEach(p => { delete payload[URL_PORTAL_MAP[p].field]; });

    if (URL_PORTAL_MAP[form.portal]) {
      const cfg  = URL_PORTAL_MAP[form.portal];
      const urls = (form[cfg.field] || '').split(/[\n,]+/).map(u => u.trim()).filter(u => u.startsWith('http'));
      if (!urls.length) {
        await dialogs.alert(`⚠️ Añade al menos una URL de ${form.portal} (ej: ${cfg.placeholder})`);
        return;
      }
      payload.poblaciones = urls.map(u => { try { return new URL(u).pathname.split('/').filter(Boolean).pop() || u; } catch { return u; } });
      payload.extras      = { ...payload.extras, [cfg.firstKey]: urls[0], [cfg.extrasKey]: urls };
    } else {
      if (!form.poblaciones.length) {
        setError('Selecciona al menos una población.');
        await dialogs.alert('⚠️ Selecciona al menos una población antes de guardar.');
        return;
      }
    }

    setGuardando(true); setError('');
    try {
      if (editandoId) {
        await api.put(`/scraping/busquedas/${editandoId}`, payload);
        mostrarExito('Búsqueda actualizada.');
      } else {
        await api.post('/scraping/busquedas', payload);
        mostrarExito('Búsqueda guardada.');
      }
      setForm({ ...BUSQUEDA_VACIA }); setEditandoId(null);
      await cargarDatos();
    } catch (e) {
      setError('Error: ' + (e.response?.data?.error || e.message));
    } finally { setGuardando(false); }
  };

  const editarBusqueda = async b => {
    setFiltroPortal(b.portal || 'fotocasa');
    const provId = provincias.find(p => p.nombre.toLowerCase() === (b.provincia || '').toLowerCase())?.id || '';
    if (provId) {
      const r = await api.get(`/scraping/poblaciones?provincia_id=${provId}`).catch(() => ({ data: [] }));
      setPoblaciones(r.data);
    }
    setEditandoId(b.id);
    const urlFields = {};
    Object.entries(URL_PORTAL_MAP).forEach(([portal, cfg]) => {
      const ex = b.extras || {};
      urlFields[cfg.field] = (ex[cfg.extrasKey] || (ex[cfg.firstKey] ? [ex[cfg.firstKey]] : [])).join('\n');
    });
    setForm({
      nombre: b.nombre, portal: b.portal || 'fotocasa', operacion: b.operacion || 'comprar',
      tipo: b.tipo || '', provincia: b.provincia || '', provincia_id: provId,
      poblaciones: b.poblaciones || [], precio_min: b.precio_min || '', precio_max: b.precio_max || '',
      hab_min: b.hab_min || '', banos_min: b.banos_min || '', m2_min: b.m2_min || '', m2_max: b.m2_max || '',
      parcela_min: b.parcela_min || '', parcela_max: b.parcela_max || '',
      solo_particulares: b.solo_particulares === true, extras: b.extras || {}, activo: b.activo !== false,
      ...urlFields,
    });
  };

  const cancelarEdicion = () => { setEditandoId(null); setForm({ ...BUSQUEDA_VACIA }); setError(''); };

  // Guardar un solo campo de una búsqueda existente sin abrir el formulario completo
  const guardarCampoInline = async (busqueda, campo, valor) => {
    try {
      const payload = { ...busqueda, [campo]: valor };
      await api.put(`/scraping/busquedas/${busqueda.id}`, payload);
      await cargarDatos();
    } catch (e) {
      await dialogs.alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    }
  };

  const eliminarBusqueda = async id => {
    if (!(await dialogs.confirm('¿Eliminar esta búsqueda?'))) return;
    try { await api.delete(`/scraping/busquedas/${id}`); mostrarExito('Eliminada.'); await cargarDatos(); }
    catch (e) { setError('Error: ' + e.message); }
  };

  const ejecutarBusqueda = async id => {
    setEjecutando(e => ({ ...e, [id]: true }));
    setJobEstados(s => ({ ...s, [id]: { estado: 'iniciando', msg: 'Iniciando scraping…', total: 0, procesados: 0, nuevos: 0 } }));
    try {
      await api.post(`/scraping/ejecutar/${id}`);
      // Iniciar polling para estado en tiempo real
      const startedAt = Date.now();
      const pollInterval = setInterval(async () => {
        try {
          const r = await api.get(`/scraping/estado/${id}`);
          const est = r.data;
          // Solo actualizar si el backend ya tiene estado real (no inactivo inicial)
          if (est?.estado && est.estado !== 'inactivo') {
            setJobEstados(s => ({ ...s, [id]: est }));
          }
          const elapsed = Date.now() - startedAt;
          // Detener solo si el job terminó, falló, o lleva >90s sin actividad
          if (est?.estado === 'terminado' || est?.estado === 'error') {
            clearInterval(pollInterval);
            delete pollingRefs.current[id];
            setEjecutando(e => ({ ...e, [id]: false }));
            if (est?.estado === 'terminado') {
              mostrarExito(`✅ ${est.msg || 'Scraping completado'}`);
              await cargarDatos();
            }
          } else if (est?.estado === 'inactivo' && elapsed > 90000) {
            // Después de 90s sin actividad, asumir que terminó silenciosamente
            clearInterval(pollInterval);
            delete pollingRefs.current[id];
            setEjecutando(e => ({ ...e, [id]: false }));
            setJobEstados(s => ({ ...s, [id]: { estado: 'terminado', msg: 'Completado (sin estado explícito)' } }));
            await cargarDatos();
          }
        } catch { /* ignorar errores de polling */ }
      }, 1500);
      pollingRefs.current[id] = pollInterval;
    } catch (e) {
      setError('Error: ' + e.message);
      setEjecutando(e => ({ ...e, [id]: false }));
      setJobEstados(s => ({ ...s, [id]: { estado: 'error', msg: e.message } }));
    }
  };

  const guardarConfig = async () => {
    if (!modCredenciales) return;
    setGuardandoCfg(true);
    try { await api.put('/scraping/config', config); mostrarExito('Configuración guardada.'); }
    catch (e) { setError('Error: ' + e.message); }
    finally { setGuardandoCfg(false); }
  };

  // ── Estilos base ──────────────────────────────────────────────────────────────
  const inp = {
    width: '100%', backgroundColor: t.inputBg, border: `1.5px solid ${t.inputBorder}`,
    borderRadius: 6, padding: '5px 8px', color: t.textPrimary,
    fontSize: 12, outline: 'none', boxSizing: 'border-box',
  };
  const lbl = { display: 'block', fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const btnPrim = { padding: '6px 14px', backgroundColor: t.accentBlue, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  const btnSec  = { padding: '6px 12px', backgroundColor: 'transparent', color: t.textMuted, border: `1px solid ${t.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      backgroundColor: t.bgApp, color: t.textPrimary, overflow: 'hidden',
    }}>

      {/* ── Barra de título ── */}
      <div style={{
        padding: '10px 20px', borderBottom: `1px solid ${t.border}`,
        backgroundColor: t.bgPanel, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🕷️</span>
          <div>
            <span style={{ fontSize: 15, fontWeight: 800, color: t.textPrimary }}>Configuración Scraping</span>
            <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 10 }}>
              {busquedas.length} búsqueda{busquedas.length !== 1 ? 's' : ''} activa{busquedas.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(error || exito) && (
            <span style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 5,
              backgroundColor: error ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)',
              color: error ? t.accentRed : t.accentGreen,
              border: `1px solid ${error ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
            }}>
              {error || exito}
            </span>
          )}
          <button onClick={() => api.post('/scraping/ejecutar-todas').then(() => mostrarExito('Scraping iniciado en todas las búsquedas.'))}
            style={{ ...btnPrim, display: 'flex', alignItems: 'center', gap: 5 }}>
            ▶ Ejecutar todas
          </button>
        </div>
      </div>

      {/* ── Header: Credenciales (banda compacta) ── */}
      <div style={{
        backgroundColor: t.bgCard, borderBottom: `1px solid ${t.border}`,
        padding: '6px 20px', flexShrink: 0,
      }}>
        {/* Fila única: estado por portal + botones de acción */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Estado de cada portal */}
          {veCookies && PORTALES_CONFIG.map(portal => {
            const updatedAt = config[portal.campo_updated];
            const hasCookie = !!(config[portal.campo_cookie] && String(config[portal.campo_cookie]).trim().length > 20);
            const hasCreds = !!(config[portal.campo_email] && config[portal.campo_pass]);
            const ok = updatedAt || hasCookie || hasCreds;
            return (
              <div key={portal.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, fontSize: 11, backgroundColor: ok ? 'rgba(34,197,94,0.10)' : t.bgPanel, border: `1px solid ${ok ? 'rgba(34,197,94,0.3)' : t.border}` }}>
                <span>{portal.emoji}</span>
                <span style={{ fontWeight: 600, color: t.textPrimary }}>{portal.nombre}</span>
                {ok
                  ? (
                    <span style={{ color: t.accentGreen }}>
                      ✅{' '}
                      {updatedAt
                        ? new Date(updatedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : hasCookie
                          ? 'cookie activa'
                          : 'credenciales'}
                    </span>
                  )
                  : <span style={{ color: t.textMuted }}>sin configurar</span>
                }
              </div>
            );
          })}

          {/* Separador flexible */}
          <div style={{ flex: 1 }} />

          {/* Botones de acción */}
          {veCredenciales && (<>
          <button onClick={() => setShowCredenciales(v => !v)}
            style={{ ...btnSec, padding: '3px 10px', fontSize: 11, backgroundColor: showCredenciales ? t.accentBlueLight : undefined, color: showCredenciales ? t.accentBlue : undefined }}>
            🔐 {showCredenciales ? 'Ocultar' : 'Credenciales'}
          </button>
          <button onClick={probarIdealistaSesion} disabled={testandoIdealista}
            title="Comprueba la cookie Idealista guardada"
            style={{ ...btnSec, padding: '3px 10px', fontSize: 11, opacity: testandoIdealista ? 0.6 : 1 }}>
            {testandoIdealista ? '⏳' : '📞'} Sesión Idealista
          </button>
          <button onClick={probarConexion} disabled={testando}
            style={{ ...btnSec, padding: '3px 10px', fontSize: 11, opacity: testando ? 0.6 : 1 }}>
            {testando ? '⏳' : '🔍'} Conexión
          </button>
          <button onClick={renovarCookies} disabled={renovando || !modCredenciales}
            title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : 'Solo Fotocasa. Idealista: cookie manual + Sesión Idealista'}
            style={{ ...btnSec, padding: '3px 10px', fontSize: 11, opacity: (renovando || !modCredenciales) ? 0.7 : 1 }}>
            {renovando ? '⏳' : '🔄'} Renovar
          </button>
          <button onClick={guardarConfig} disabled={guardandoCfg || !modCredenciales}
            title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined}
            style={{ ...btnPrim, padding: '3px 10px', fontSize: 11, opacity: (guardandoCfg || !modCredenciales) ? 0.6 : 1 }}>
            {guardandoCfg ? '…' : '💾'} Guardar
          </button>
          </>)}
        </div>

        {/* Panel colapsable de credenciales */}
        {showCredenciales && veCredenciales && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${t.borderLight}` }}>
            {/* Tabla de portales */}
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 180px', gap: 8, padding: '0 4px' }}>
                {['Portal', 'Email de acceso', 'Contraseña', ...(veCookies ? ['Estado'] : [])].map(h => (
                  <span key={h} style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
                ))}
              </div>
              {PORTALES_CONFIG.map(portal => {
                const updatedAt = config[portal.campo_updated];
                return (
                  <div key={portal.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 180px', gap: 8, alignItems: 'center', padding: '4px', borderRadius: 6, backgroundColor: t.bgPanel }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{portal.emoji}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary }}>{portal.nombre}</span>
                    </div>
                    <input type="email" style={{ ...inp, fontSize: 12 }}
                      value={config[portal.campo_email] || ''}
                      onChange={e => setConfig(c => ({ ...c, [portal.campo_email]: e.target.value }))}
                      disabled={!modCredenciales} title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined}
                      placeholder="usuario@ejemplo.com" />
                    <input type="password" style={{ ...inp, fontSize: 12 }}
                      value={config[portal.campo_pass] || ''}
                      onChange={e => setConfig(c => ({ ...c, [portal.campo_pass]: e.target.value }))}
                      disabled={!modCredenciales} title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined}
                      placeholder="••••••••" />
                    {veCookies && (
                    <div style={{ fontSize: 11 }}>
                      {updatedAt
                        ? <span style={{ color: t.accentGreen }}>✅ {new Date(updatedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        : config[portal.campo_cookie]
                          ? <span style={{ color: t.accentGreen }}>✅ cookie activa</span>
                          : (config[portal.campo_email] && config[portal.campo_pass])
                            ? portal.id === 'idealista'
                              ? <span style={{ color: t.accentOrange }} title="Renovar no funciona en Idealista — pega cookie abajo + Guardar">🟡 pega cookie + Guardar</span>
                              : <span style={{ color: t.accentOrange }} title="Pulsa 🔄 Renovar para crear la sesión (cookie)">🟡 pendiente — pulsa Renovar</span>
                            : <span style={{ color: t.textMuted }}>— sin configurar</span>
                      }
                    </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Cookie Fotocasa manual */}
            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, alignItems: 'start' }}>
              <div>
                <label style={{ ...lbl, marginBottom: 3 }}>
                  Cookie Fotocasa (manual)
                  <button type="button" onClick={() => setShowCookieHelp(v => !v)}
                    style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10, borderRadius: 10, border: `1px solid ${t.border}`, backgroundColor: 'transparent', color: t.textMuted, cursor: 'pointer' }}>
                    {showCookieHelp ? '✕ ocultar ayuda' : '? cómo obtener'}
                  </button>
                </label>
                {showCookieHelp && (
                  <div style={{ marginBottom: 5, padding: '7px 10px', borderRadius: 6, backgroundColor: t.bgPanel, border: `1px solid ${t.border}`, fontSize: 11, color: t.textSecondary, lineHeight: 1.7 }}>
                    F12 → <strong>Network</strong> → recarga → clic en cualquier petición fotocasa.es → <strong>Headers → Request Headers → Cookie</strong> → copia todo el valor y pega aquí.<br />
                    <span style={{ color: t.accentOrange, fontSize: 10 }}>⚠️ No uses Console → copy(document.cookie): no captura HttpOnly.</span>
                    {syncEstado?.cookieIngestConfigured && (
                      <span style={{ color: t.accentGreen, display: 'block', marginTop: 4 }}>
                        ✅ Extensión Chrome activa — las cookies se renuevan automáticamente cada ~20 min.
                      </span>
                    )}
                  </div>
                )}
                <input type="text" style={{ ...inp, fontFamily: 'monospace', fontSize: 10 }}
                  value={config.fotocasa_cookie_string || ''}
                  onChange={e => setConfig(c => ({ ...c, fotocasa_cookie_string: e.target.value }))}
                  disabled={!modCredenciales} title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined}
                  placeholder=".ASPXFORMS=...; logincookie=...; reese84=..." />
              </div>
            </div>

            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
              <div style={{
                padding: '8px 10px', borderRadius: 6, fontSize: 11, lineHeight: 1.6,
                backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: t.accentRed,
              }}>
                <strong>No pegues cookie Idealista en producción.</strong> Si el servidor la usa, Idealista bloquea tu cuenta
                (sesión Chrome + IP Railway). Datos de ficha: ScraperAPI sin login. Teléfono: extensión{' '}
                <strong>Idealista Phone Bridge</strong> (v1.1+).
              </div>
              <label style={{ ...lbl, marginBottom: 3, opacity: 0.7 }}>
                Cookie Idealista (solo pruebas locales — IDEALISTA_USAR_COOKIE_EN_SERVIDOR=1)
                <button type="button" onClick={() => setShowIdealistaCookieHelp(v => !v)}
                  style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10, borderRadius: 10, border: `1px solid ${t.border}`, backgroundColor: 'transparent', color: t.textMuted, cursor: 'pointer' }}>
                  {showIdealistaCookieHelp ? '✕ ocultar ayuda' : '? cómo obtener'}
                </button>
              </label>
              {showIdealistaCookieHelp && (
                <div style={{ marginBottom: 5, padding: '7px 10px', borderRadius: 6, backgroundColor: t.bgPanel, border: `1px solid ${t.border}`, fontSize: 11, color: t.textSecondary, lineHeight: 1.7 }}>
                  1. Con sesión iniciada, abre la ficha en idealista.com.<br />
                  2. F12 → <strong>Network</strong> → <strong>F5</strong> (recargar).<br />
                  3. Clic en la <strong>primera fila</strong> de la lista: tipo <strong>document</strong>, nombre <strong>111444857</strong> (el HTML de la ficha), dominio <strong>www.idealista.com</strong>. No uses peticiones a clarity.ms, ads, etc.<br />
                  4. <strong>Headers</strong> → <strong>Request Headers</strong> → línea <strong>cookie:</strong> → doble clic en el valor largo → Ctrl+C.<br />
                  5. Pégalo en el campo de abajo → <strong>💾 Guardar</strong> → <strong>📞 Sesión Idealista</strong>.<br />
                  <span style={{ color: t.accentOrange, fontSize: 10 }}>⚠️ No uses Console ni «Renovar». No copies peticiones que no sean document de www.idealista.com.</span>
                </div>
              )}
              <input type="text" style={{ ...inp, fontFamily: 'monospace', fontSize: 10 }}
                value={config.idealista_cookie_string || ''}
                onChange={e => setConfig(c => ({ ...c, idealista_cookie_string: e.target.value }))}
                disabled={!modCredenciales} title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined}
                placeholder="F12 → Network → idealista.com → Request Headers → Cookie" />
              {testIdealistaResult && (
                <div style={{
                  fontSize: 11, padding: '6px 10px', borderRadius: 6,
                  backgroundColor: testIdealistaResult.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  color: testIdealistaResult.ok ? t.accentGreen : t.accentRed,
                  border: `1px solid ${testIdealistaResult.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                }}>
                  {testIdealistaResult.msg || (testIdealistaResult.ok ? 'Sesión OK' : 'Sin sesión')}
                  {testIdealistaResult.phone_test?.telefono && (
                    <span> · teléfono directo: {testIdealistaResult.phone_test.telefono}</span>
                  )}
                  {testIdealistaResult.phone_test_scraper?.telefono && (
                    <span> · ScraperAPI: {testIdealistaResult.phone_test_scraper.telefono}</span>
                  )}
                  {testIdealistaResult.ok && !testIdealistaResult.phone_test?.telefono && !testIdealistaResult.phone_test_scraper?.telefono && (
                    <span> · teléfono: usar extensión Idealista Phone Bridge</span>
                  )}
                </div>
              )}
              <p style={{ fontSize: 10, color: t.textMuted, margin: 0, lineHeight: 1.5 }}>
                <strong>Captaciones Ana:</strong> instala <strong>Idealista Phone Bridge</strong> (carpeta <code style={{ fontSize: 9 }}>extensions/idealista-phone-bridge</code>, v1.2+).
                Con sesión en Idealista y <em>cola automática</em> activa, rellena teléfonos en segundo plano y crea precaptaciones para Ana.
                Endpoint: <code style={{ fontSize: 9 }}>/api/public/idealista-telefono</code>
              </p>
            </div>

            {/* Config avanzada */}
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setShowAdvanced(v => !v)}
                style={{ ...btnSec, padding: '2px 8px', fontSize: 10 }}>
                ⚙️ {showAdvanced ? '▲' : '▼'} Ajustes avanzados
              </button>
            </div>
            {showAdvanced && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                <div>
                  <label style={lbl}>Pausa entre peticiones (ms)</label>
                  <input type="number" style={inp} min="800" max="10000" step="100"
                    value={config.delay_entre_peticiones_ms || '1500'}
                    onChange={e => setConfig(c => ({ ...c, delay_entre_peticiones_ms: e.target.value }))}
                    disabled={!modCredenciales} title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined} />
                </div>
                <div>
                  <label style={lbl}>Máx. resultados por búsqueda</label>
                  <input type="number" style={inp} min="5" max="100"
                    value={config.max_resultados_por_busqueda || '20'}
                    onChange={e => setConfig(c => ({ ...c, max_resultados_por_busqueda: e.target.value }))}
                    disabled={!modCredenciales} title={!modCredenciales ? 'No tienes permiso para modificar credenciales' : undefined} />
                </div>
              </div>
            )}

            {/* Resultados de test / renovación */}
            {testResult && (
              <div style={{ marginTop: 6, padding: '6px 12px', borderRadius: 6, fontSize: 11, backgroundColor: testResult.ok ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)', border: `1px solid ${testResult.ok ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`, color: testResult.ok ? t.accentGreen : t.accentRed }}>
                {testResult.ok ? '✅' : '❌'} {testResult.mensaje}
              </div>
            )}
            {renovResult && (
              <div style={{ marginTop: 4, padding: '6px 12px', borderRadius: 6, fontSize: 11, backgroundColor: renovResult.ok === true ? 'rgba(34,197,94,0.10)' : renovResult.ok === false ? 'rgba(239,68,68,0.10)' : 'rgba(234,179,8,0.10)', border: `1px solid ${renovResult.ok === true ? 'rgba(34,197,94,0.35)' : renovResult.ok === false ? 'rgba(239,68,68,0.35)' : 'rgba(234,179,8,0.35)'}`, color: renovResult.ok === true ? t.accentGreen : renovResult.ok === false ? t.accentRed : t.accentOrange }}>
                {renovResult.mensaje}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Cuerpo: 2 columnas ── */}
      <div style={{ flex: 1, display: 'flex', gap: 0, overflow: 'hidden' }}>

        {/* ── Columna izquierda: formulario ── */}
        <div style={{
          flex: '0 0 50%', minWidth: 0, borderRight: `1px solid ${t.border}`,
          backgroundColor: t.bgCard, overflowY: 'auto', padding: '14px 16px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary }}>
              {editandoId ? '✏️ Editar búsqueda' : '➕ Nueva búsqueda'}
            </span>
            {editandoId && <button onClick={cancelarEdicion} style={btnSec}>× Cancelar</button>}
          </div>

          {/* Portal */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {[
              {v:'fotocasa',    label:'🔶 Fotocasa'},
              {v:'idealista',   label:'🏠 Idealista'},
              {v:'pisos',       label:'🔑 Pisos.com'},
              {v:'kyero',       label:'🌿 Kyero'},
              {v:'habitaclia',  label:'🏘️ Habitaclia'},
              {v:'milanuncios', label:'📋 Milanuncios'},
              {v:'thinkspain',  label:'🇬🇧 ThinkSpain'},
              {v:'todopisos',   label:'🏡 Todopisos'},
            ].map(({v, label}) => {
              const pc = PORTAL_COLORS[v] || PORTAL_COLORS.fotocasa;
              return (
                <button key={v} type="button" onClick={() => { setField('portal', v); setFiltroPortal(v); }} style={{
                  flex: '1 1 calc(25% - 4px)', minWidth: 80, padding: '5px 4px', fontSize: 10, fontWeight: 700,
                  borderRadius: 7, cursor: 'pointer', border: '1.5px solid',
                  backgroundColor: form.portal === v ? pc.bg     : t.bgCard,
                  borderColor:     form.portal === v ? pc.border  : t.border,
                  color:           form.portal === v ? pc.text    : t.textMuted,
                }}>{label}</button>
              );
            })}
          </div>

          {/* Nombre */}
          <div>
            <label style={lbl}>Nombre *</label>
            <input type="text" style={inp} placeholder={form.portal === 'idealista' ? 'Ej: General Idealista' : 'Ej: Chalets Dénia piscina'}
              value={form.nombre} onChange={e => setField('nombre', e.target.value)} />
          </div>

          {URL_PORTAL_MAP[form.portal] ? (<>
            {/* URLs para portales basados en URL */}
            {(() => {
              const cfg = URL_PORTAL_MAP[form.portal];
              const pc  = PORTAL_COLORS[form.portal] || PORTAL_COLORS.fotocasa;
              const portalLabel = { idealista:'Idealista', pisos:'Pisos.com', kyero:'Kyero', habitaclia:'Habitaclia', milanuncios:'Milanuncios', thinkspain:'ThinkSpain', todopisos:'Todopisos' }[form.portal] || form.portal;
              const val = form[cfg.field] || '';
              const n   = val.split(/[\n,]+/).filter(u => u.trim().startsWith('http')).length;
              return (
                <div>
                  <label style={lbl}>URLs de {portalLabel} * <span style={{ fontWeight:400, color: t.textMuted }}>(una por línea, una por pueblo)</span></label>
                  <textarea
                    value={val}
                    onChange={e => setField(cfg.field, e.target.value)}
                    placeholder={cfg.placeholder}
                    rows={4}
                    style={{ ...inp, resize: 'vertical', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6 }}
                  />
                  {n > 0 && <span style={{ fontSize:10, color: pc.text }}>✓ {n} URL{n>1?'s':''} configurada{n>1?'s':''}</span>}
                </div>
              );
            })()}
          </>) : (<>
            {/* Operación + Tipo — solo Fotocasa */}
            {veBusqueda && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>Operación</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={form.operacion} onChange={e => setField('operacion', e.target.value)}
                  disabled={!modBusqueda} title={!modBusqueda ? 'No tienes permiso para modificar operación/tipo/extras' : undefined}>
                  {OPERACIONES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Tipo</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={form.tipo} onChange={e => setField('tipo', e.target.value)}
                  disabled={!modBusqueda} title={!modBusqueda ? 'No tienes permiso para modificar operación/tipo/extras' : undefined}>
                  {TIPOS_INMUEBLE.map(ti => <option key={ti.value} value={ti.value}>{ti.label}</option>)}
                </select>
              </div>
            </div>
            )}

            {/* Provincia + Poblaciones */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>Provincia *</label>
                <AutocompleteProvincia value={form.provincia} provincias={provincias} t={t}
                  onChange={v => setField('provincia', v)}
                  onSeleccionada={id => { setField('provincia_id', id); setField('poblaciones', []); }} />
              </div>
              <div>
                <label style={lbl}>Poblaciones * {form.poblaciones.length > 0 && <span style={{ fontWeight: 400 }}>({form.poblaciones.length})</span>}</label>
                <MultiSelectPoblaciones poblaciones={poblaciones} seleccionadas={form.poblaciones} onChange={v => setField('poblaciones', v)} t={t} />
              </div>
            </div>
          </>)}

          {/* Precio, hab, m², parcela, extras — solo Fotocasa */}
          {form.portal === 'fotocasa' && <>
            <div>
              <label style={lbl}>Precio (€)</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="number" style={inp} placeholder="Mín" value={form.precio_min} onChange={e => setField('precio_min', e.target.value)} min="0" step="5000" />
                <span style={{ color: t.textMuted, fontSize: 12, flexShrink: 0 }}>—</span>
                <input type="number" style={inp} placeholder="Máx" value={form.precio_max} onChange={e => setField('precio_max', e.target.value)} min="0" step="5000" />
              </div>
            </div>

            {/* Hab / Baños / M2 / Parcela — grid 2x2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>Hab. mín.</label>
                <input type="number" style={inp} placeholder="—" value={form.hab_min} onChange={e => setField('hab_min', e.target.value)} min="1" max="10" />
              </div>
              <div>
                <label style={lbl}>Baños mín.</label>
                <input type="number" style={inp} placeholder="—" value={form.banos_min} onChange={e => setField('banos_min', e.target.value)} min="1" max="6" />
              </div>
              <div>
                <label style={lbl}>M² construidos</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="number" style={{ ...inp, padding: '5px 4px', textAlign: 'center' }} placeholder="Mín" value={form.m2_min} onChange={e => setField('m2_min', e.target.value)} min="0" />
                  <span style={{ color: t.textMuted, fontSize: 10, alignSelf: 'center' }}>—</span>
                  <input type="number" style={{ ...inp, padding: '5px 4px', textAlign: 'center' }} placeholder="Máx" value={form.m2_max} onChange={e => setField('m2_max', e.target.value)} min="0" />
                </div>
              </div>
              <div>
                <label style={lbl}>Parcela (m²)</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="number" style={{ ...inp, padding: '5px 4px', textAlign: 'center' }} placeholder="Mín" value={form.parcela_min} onChange={e => setField('parcela_min', e.target.value)} min="0" />
                  <span style={{ color: t.textMuted, fontSize: 10, alignSelf: 'center' }}>—</span>
                  <input type="number" style={{ ...inp, padding: '5px 4px', textAlign: 'center' }} placeholder="Máx" value={form.parcela_max} onChange={e => setField('parcela_max', e.target.value)} min="0" />
                </div>
              </div>
            </div>

            {veBusqueda && (
            <div>
              <label style={lbl}>Extras</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {EXTRAS_LISTA.map(ex => (
                  <button key={ex.key} type="button" onClick={() => modBusqueda && toggleExtra(ex.key)} disabled={!modBusqueda}
                    title={!modBusqueda ? 'No tienes permiso para modificar operación/tipo/extras' : undefined} style={{
                    display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px',
                    borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: modBusqueda ? 'pointer' : 'not-allowed', border: '1.5px solid',
                    backgroundColor: form.extras[ex.key] ? t.accentBlue  : t.chipBg,
                    borderColor:     form.extras[ex.key] ? t.accentBlue  : t.chipBorder,
                    color:           form.extras[ex.key] ? '#fff'        : t.chipText,
                    opacity: modBusqueda ? 1 : 0.6,
                  }}>
                    {ex.icon} {ex.label}
                  </button>
                ))}
              </div>
            </div>
            )}
          </>}

          {/* Toggles */}
          <div style={{ display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, color: t.textPrimary }}>
              <Toggle value={form.solo_particulares} onChange={v => setField('solo_particulares', v)} t={t} />
              Solo particulares
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, color: t.textPrimary }}>
              <Toggle value={form.activo} onChange={v => setField('activo', v)} t={t} />
              Activa
            </label>
          </div>

          {/* Botón guardar */}
          <div style={{ paddingTop: 4 }}>
            <button onClick={guardarBusqueda} disabled={guardando}
              style={{ ...btnPrim, width: '100%', padding: '8px', opacity: guardando ? 0.6 : 1 }}>
              {guardando ? 'Guardando…' : editandoId ? '✔ Actualizar búsqueda' : '💾 Guardar búsqueda'}
            </button>
          </div>
        </div>

        {/* ── Columna derecha: búsquedas guardadas ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: t.bgApp }}>

          {/* Filtros por portal */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderBottom: `1px solid ${t.border}`, backgroundColor: t.bgPanel, flexShrink: 0, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 2 }}>Filtrar:</span>
            {[
              { v: 'todos',       label: 'Todos',           color: t.textMuted, bg: t.bgCard },
              { v: 'fotocasa',    label: '🔶 Fotocasa',     color: '#f97316',   bg: 'rgba(249,115,22,0.12)' },
              { v: 'idealista',   label: '🏠 Idealista',    color: '#0ea5e9',   bg: 'rgba(14,165,233,0.12)' },
              { v: 'pisos',       label: '🔑 Pisos.com',    color: '#a855f7',   bg: 'rgba(168,85,247,0.12)' },
              { v: 'kyero',       label: '🌿 Kyero',        color: '#10b981',   bg: 'rgba(16,185,129,0.12)' },
              { v: 'habitaclia',  label: '🏘️ Habitaclia',  color: '#06b6d4',   bg: 'rgba(6,182,212,0.12)'  },
              { v: 'milanuncios', label: '📋 Milanuncios',  color: '#f59e0b',   bg: 'rgba(245,158,11,0.12)' },
              { v: 'thinkspain',  label: '🇬🇧 ThinkSpain', color: '#ec4899',   bg: 'rgba(236,72,153,0.12)' },
              { v: 'todopisos',   label: '🏡 Todopisos',    color: '#84cc16',   bg: 'rgba(132,204,22,0.12)' },
            ].map(({ v, label, color, bg }) => {
              const count = v === 'todos' ? busquedas.length : busquedas.filter(b => (b.portal || 'fotocasa') === v).length;
              const active = filtroPortal === v;
              return (
                <button key={v} onClick={() => setFiltroPortal(v)} style={{
                  padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: active ? 700 : 500,
                  cursor: 'pointer', border: `1.5px solid ${active ? color : t.border}`,
                  backgroundColor: active ? bg : 'transparent',
                  color: active ? color : t.textMuted,
                }}>
                  {label} <span style={{ fontSize: 9, opacity: 0.7 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {busquedas.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: t.textMuted, gap: 8 }}>
              <span style={{ fontSize: 32 }}>🔍</span>
              <span style={{ fontSize: 13 }}>No hay búsquedas guardadas.</span>
              <span style={{ fontSize: 11 }}>Crea la primera usando el formulario de la izquierda.</span>
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ backgroundColor: t.bgPanel, position: 'sticky', top: 0, zIndex: 1 }}>
                  {['Nombre', 'Op.', 'Tipo', 'Poblaciones', 'Precio', 'Hab.', 'Extras', 'Part.', 'Results.', 'Última ejec.', 'Estado', ''].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {busquedas.filter(b => filtroPortal === 'todos' || (b.portal || 'fotocasa') === filtroPortal).map((b, i) => (
                  <tr key={b.id}
                    style={{ borderBottom: `1px solid ${t.borderLight}`, backgroundColor: i % 2 === 0 ? 'transparent' : t.bgPanel + '20' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = t.bgCardHover}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = i % 2 === 0 ? 'transparent' : t.bgPanel + '20'}
                  >
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: t.textPrimary, maxWidth: 150 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        {(() => {
                          const pc = PORTAL_COLORS[b.portal] || PORTAL_COLORS.fotocasa;
                          const letter = PORTAL_BADGE[b.portal] || 'F';
                          return <span style={{ fontSize:9, padding:'1px 4px', backgroundColor: pc.bg, color: pc.text, borderRadius:3, fontWeight:700, flexShrink:0 }}>{letter}</span>;
                        })()}
                        <span title={b.nombre} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.nombre}</span>
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', color: t.textMuted, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{b.operacion}</td>
                    <td style={{ padding: '8px 10px', color: t.textMuted, textTransform: 'capitalize' }}>{b.tipo || '—'}</td>
                    <td style={{ padding: '8px 10px', maxWidth: 160 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {(b.poblaciones || []).slice(0, 2).map(p => (
                          <span key={p} style={{ backgroundColor: t.accentBlueLight, color: t.accentBlue, borderRadius: 3, padding: '1px 5px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>{p}</span>
                        ))}
                        {(b.poblaciones || []).length > 2 && (
                          <span style={{ color: t.textMuted, fontSize: 10 }} title={(b.poblaciones || []).join(', ')}>+{b.poblaciones.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', color: t.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>
                      {b.precio_min || b.precio_max
                        ? `${b.precio_min ? (Number(b.precio_min)/1000).toFixed(0)+'k' : ''} — ${b.precio_max ? (Number(b.precio_max)/1000).toFixed(0)+'k' : '∞'}`
                        : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: t.textMuted, textAlign: 'center' }}>{b.hab_min ? `+${b.hab_min}` : '—'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {Object.entries(b.extras || {}).filter(([, v]) => v).map(([k]) => (
                          <span key={k} style={{ fontSize: 12 }}>{EXTRAS_LISTA.find(e => e.key === k)?.icon}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                      <div title={b.solo_particulares ? 'Solo particulares: ON' : 'Solo particulares: OFF'}>
                        <Toggle value={!!b.solo_particulares} onChange={v => guardarCampoInline(b, 'solo_particulares', v)} t={t} />
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                      <span style={{ fontWeight: 700, color: t.accentBlue }}>{b.total_resultados || 0}</span>
                      {b.nuevos_hoy > 0 && <span style={{ color: t.accentGreen, fontSize: 10, marginLeft: 3 }}>+{b.nuevos_hoy}</span>}
                    </td>
                    <td style={{ padding: '8px 10px', color: t.textMuted, fontSize: 10, whiteSpace: 'nowrap' }}>
                      {b.ultima_ejecucion
                        ? new Date(b.ultima_ejecucion).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : 'Nunca'}
                    </td>
                    <td style={{ padding: '8px 10px', minWidth: 140 }}>
                      {(() => {
                        const je = jobEstados[b.id];
                        if (je && je.estado !== 'inactivo') {
                          const pct = je.total > 0 ? Math.round((je.procesados / je.total) * 100) : null;
                          const colorEst = je.estado === 'terminado' ? t.accentGreen : je.estado === 'error' ? t.accentRed : t.accentBlue;
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {pct !== null && (
                                <div style={{ height: 4, backgroundColor: t.border, borderRadius: 2, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: pct + '%', backgroundColor: colorEst, borderRadius: 2, transition: 'width 0.3s ease' }} />
                                </div>
                              )}
                              <span style={{ fontSize: 10, color: colorEst, lineHeight: 1.3 }} title={je.msg}>
                                {je.estado === 'terminado' && '✅ '}
                                {je.estado === 'error'     && '❌ '}
                                {je.estado !== 'terminado' && je.estado !== 'error' && '⏳ '}
                                {je.msg?.length > 45 ? je.msg.substring(0, 45) + '…' : (je.msg || je.estado)}
                              </span>
                              {je.nuevos > 0 && (
                                <span style={{ fontSize: 10, color: t.accentGreen, fontWeight: 700 }}>
                                  +{je.nuevos} nuevos
                                </span>
                              )}
                            </div>
                          );
                        }
                        return (
                          <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 600, backgroundColor: b.activo ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)', color: b.activo ? t.accentGreen : t.textMuted }}>
                            {b.activo ? 'Activa' : 'Pausa'}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => ejecutarBusqueda(b.id)} disabled={ejecutando[b.id]} title="Ejecutar"
                          style={{ padding: '3px 7px', backgroundColor: 'rgba(34,197,94,0.12)', color: t.accentGreen, border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, opacity: ejecutando[b.id] ? 0.5 : 1 }}>
                          {ejecutando[b.id] ? '⏳' : '▶'}
                        </button>
                        <button onClick={() => editarBusqueda(b)} title="Editar"
                          style={{ padding: '3px 7px', backgroundColor: t.accentBlueLight, color: t.accentBlue, border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>✏️</button>
                        <button onClick={() => eliminarBusqueda(b.id)} title="Eliminar"
                          style={{ padding: '3px 7px', backgroundColor: 'rgba(239,68,68,0.10)', color: t.accentRed, border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
